import { API_BASE_URL, pageHeader, useApiToken } from "@appretio/api";
import { useQuery } from "@tanstack/react-query";
import { Conversation } from "../types";
import { chatQueryKeys } from "./queryKeys";

const EMPTY: Conversation = { conversation_id: null, turns: [] };

/**
 * Loads the user's most recent conversation so the drawer can replay it when
 * reopened. Only runs while `enabled` (the drawer is open).
 *
 * Uses a raw `fetch` (with the shared auth token) rather than the typed API
 * client, so the chat package stays independent of the generated schema.
 */
export function useConversation({ enabled = true } = {}) {
  const getToken = useApiToken();

  return useQuery({
    queryKey: chatQueryKeys.conversation,
    meta: {
      errorMessage: "Failed to load your previous conversation."
    },
    queryFn: async (): Promise<Conversation> => {
      const token = await getToken();
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        ...pageHeader()
      };
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await fetch(
        `${API_BASE_URL}/v2/chatbot/get-conversation`,
        { method: "POST", headers, body: "{}" }
      );
      if (!response.ok) {
        throw new Error(`get-conversation failed: ${response.status}`);
      }
      const payload = (await response.json()) as { result?: Conversation };
      return payload.result ?? EMPTY;
    },
    // The drawer seeds local state from this once per open; don't refetch.
    staleTime: Infinity,
    enabled
  });
}
